import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, BarChart3, Lock, RefreshCw, User, Users, X } from 'lucide-react';
import { createEventOutreachApi } from '../../lib/eventOutreachApi';
import { errorText } from '../../lib/apiErrors';
import { attentionText, eventTabHref, formatLocalDate } from '../../lib/eventWorkspace';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const EMPTY_FILTERS = {
    event_id: '', date_from: '', date_to: '', stage: '', rsvp_status: '', attendance_status: '', follow_up_state: '',
};

/** The #888 widgets. Labels say exactly what the server counted (see docs/api/runtime/openapi.md). */
const WIDGETS = [
    ['contacts_owned', 'Contacts owned', 'Contacts in scope'],
    ['contacted', 'Contacted'],
    ['follow_ups_due', 'Follow-ups due'],
    ['follow_ups_overdue', 'Overdue'],
    ['rsvp_confirmed', 'RSVP confirmed'],
    ['attended', 'Attended'],
    ['no_show', 'No-show'],
];

const EVENT_COLUMNS = [
    ['on_list', 'On list'], ['invited', 'Invited'], ['contacted', 'Contacted'], ['rsvp_confirmed', 'RSVP yes'],
    ['rsvp_declined', 'Declined'], ['rsvp_cancelled', 'Cancelled'], ['attended', 'Attended'], ['no_show', 'No-show'],
    ['follow_ups_open', 'Follow-ups'], ['follow_ups_overdue', 'Overdue'],
];

const OWNER_COLUMNS = [
    ['on_list', 'Contacts'], ['contacted', 'Reached'], ['calls', 'Calls'], ['emails', 'Emails'],
    ['follow_ups_completed', 'Follow-ups done'], ['follow_ups_overdue', 'Overdue'], ['rsvp_confirmed', 'RSVP yes'],
    ['attended', 'Attended'], ['no_show', 'No-show'],
];

const select = 'h-9 rounded-md border border-stone-200 bg-white px-2 text-sm';

function Breakdown({ title, icon: Icon, columns, rows, rowKey, rowLabel, testId, emptyText }) {
    return (
        <section className="min-w-0 rounded-xl border border-stone-200 bg-white" data-testid={testId}>
            <div className="flex items-center gap-1.5 border-b border-stone-100 px-4 py-3 text-sm font-semibold text-stone-900"><Icon className="h-4 w-4 text-stone-500" />{title}</div>
            {rows.length === 0 ? <p className="px-4 py-4 text-sm text-stone-500">{emptyText}</p> : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-xs">
                        <thead className="bg-stone-50 text-stone-500">
                            <tr>
                                <th scope="col" className="sticky left-0 bg-stone-50 px-4 py-2 font-semibold">{title === 'By event' ? 'Event' : 'Contact Owner'}</th>
                                {columns.map(([key, label]) => <th key={key} scope="col" className="whitespace-nowrap px-3 py-2 text-right font-semibold">{label}</th>)}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {rows.map((row) => (
                                <tr key={rowKey(row)}>
                                    <th scope="row" className="sticky left-0 max-w-[240px] bg-white px-4 py-2 font-medium text-stone-800">{rowLabel(row)}</th>
                                    {columns.map(([key]) => {
                                        const value = row.metrics ? row.metrics[key] : row[key];
                                        return <td key={key} className={`px-3 py-2 text-right tabular-nums ${key === 'follow_ups_overdue' && value ? 'font-semibold text-red-700' : 'text-stone-700'}`}>{value ?? 0}</td>;
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

/**
 * My Outreach (#888): read-only widgets plus event and Contact Owner
 * breakdowns from `GET /api/reports/outreach/summary`. Filters are requests;
 * the server applies visibility and clamps scope, and this component renders
 * its `effective_scope` instead of trusting its own controls.
 */
export default function MyOutreachReport({ getAuthHeader, assignedOnly }) {
    const navigate = useNavigate();
    const api = useMemo(() => createEventOutreachApi(getAuthHeader), [getAuthHeader]);
    const [scope, setScope] = useState('mine');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [eventOptions, setEventOptions] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await api.getSummary({ ...filters, scope: assignedOnly ? 'mine' : scope, limit: 1 }));
        } catch (err) {
            const status = err?.response?.status;
            setError({
                kind: status === 403 ? 'denied' : 'error',
                message: status === 403 ? 'Your role does not include outreach reports.' : errorText(err, 'My Outreach could not be loaded.'),
            });
        } finally {
            setLoading(false);
        }
    }, [api, filters, scope, assignedOnly]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/events`, { headers: getAuthHeader(), params: { page_size: 100 } })
            .then((res) => { if (!cancelled) setEventOptions(Array.isArray(res.data) ? res.data : (res.data?.data || [])); })
            .catch(() => { /* the event filter just stays empty */ });
        return () => { cancelled = true; };
    }, [getAuthHeader]);

    const setFilter = (key) => (event) => setFilters((current) => ({ ...current, [key]: event.target.value }));
    const filtered = Object.values(filters).some(Boolean);
    const effective = data?.effective_scope;
    const mine = Boolean(effective?.mine);
    const widgets = data?.widgets;

    return (
        <section className="space-y-4" data-testid="my-outreach-workspace" aria-labelledby="my-outreach-title">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 id="my-outreach-title" className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>My Outreach</h2>
                    <p className="text-xs text-stone-500">Event outreach by current Contact Owner · live from event-contact records</p>
                </div>
                <div className="flex items-center gap-2">
                    {assignedOnly || effective?.clamped ? (
                        <span className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-stone-200 bg-stone-100 px-2.5 text-xs text-stone-600" data-testid="my-outreach-scope-note"><User className="h-3.5 w-3.5" />Your contacts</span>
                    ) : (
                        <div className="flex gap-0.5 rounded-lg bg-stone-100 p-0.5" role="group" aria-label="Outreach scope">
                            {[['mine', 'Mine'], ['everyone', 'Everyone']].map(([key, label]) => (
                                <button key={key} type="button" aria-pressed={scope === key} onClick={() => setScope(key)} className={`h-7 rounded-md px-2.5 text-xs font-medium ${scope === key ? 'bg-white text-stone-900 shadow-[0_0_0_1px_#E4E1DB]' : 'text-stone-600'}`}>{label}</button>
                            ))}
                        </div>
                    )}
                    <button type="button" onClick={load} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-stone-200 px-2.5 text-xs text-stone-600 hover:bg-stone-50"><RefreshCw className="h-3.5 w-3.5" />Refresh</button>
                </div>
            </div>

            <div className="flex flex-wrap gap-2" data-testid="my-outreach-filters">
                <select aria-label="Outreach event" value={filters.event_id} onChange={setFilter('event_id')} className={`${select} min-w-0 max-w-[260px]`}>
                    <option value="">All events</option>
                    {eventOptions.map((event) => <option key={event.event_id} value={event.event_id}>{[event.event_number, event.name].filter(Boolean).join(' · ')}</option>)}
                </select>
                <label className="flex items-center gap-1 text-xs text-stone-500">Event from<input aria-label="Outreach date from" type="date" value={filters.date_from} onChange={setFilter('date_from')} className={select} /></label>
                <label className="flex items-center gap-1 text-xs text-stone-500">to<input aria-label="Outreach date to" type="date" value={filters.date_to} onChange={setFilter('date_to')} className={select} /></label>
                <select aria-label="Outreach stage" value={filters.stage} onChange={setFilter('stage')} className={select}>
                    <option value="">All stages</option>
                    {(data?.stage_options || []).map((stage) => <option key={stage.key} value={stage.key}>{stage.label}</option>)}
                </select>
                <select aria-label="Outreach RSVP" value={filters.rsvp_status} onChange={setFilter('rsvp_status')} className={select}>
                    <option value="">All RSVP</option><option value="no_response">No response</option><option value="confirmed">Confirmed</option><option value="declined">Declined</option><option value="cancelled">Cancelled</option>
                </select>
                <select aria-label="Outreach attendance" value={filters.attendance_status} onChange={setFilter('attendance_status')} className={select}>
                    <option value="">All attendance</option><option value="pending">Not marked</option><option value="attended">Attended</option><option value="no_show">No-show</option>
                </select>
                <select aria-label="Outreach follow-up" value={filters.follow_up_state} onChange={setFilter('follow_up_state')} className={select}>
                    <option value="">All follow-up</option><option value="due">Due (any open)</option><option value="overdue">Overdue</option><option value="scheduled">Scheduled</option><option value="none">None</option>
                </select>
                {filtered && <button type="button" onClick={() => setFilters(EMPTY_FILTERS)} className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm text-stone-600 hover:bg-stone-100"><X className="h-3.5 w-3.5" />Clear</button>}
            </div>

            {loading && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7" data-testid="my-outreach-loading" aria-busy="true">
                    {WIDGETS.map(([key]) => <div key={key} className="h-[74px] animate-pulse rounded-lg border border-stone-100 bg-stone-100" />)}
                </div>
            )}

            {!loading && error?.kind === 'denied' && (
                <div className="flex items-center gap-2 rounded-xl border border-stone-200 bg-white p-5 text-sm text-stone-600" role="alert" data-testid="my-outreach-denied">
                    <Lock className="h-4 w-4 text-stone-500" />{error.message}
                </div>
            )}
            {!loading && error?.kind === 'error' && (
                <div className="rounded-xl border border-red-200 bg-red-50/40 p-5 text-sm text-red-800" role="alert" data-testid="my-outreach-error">
                    <p className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{error.message}</p>
                    <button type="button" onClick={load} className="mt-3 inline-flex items-center gap-1 font-medium"><RefreshCw className="h-4 w-4" />Try again</button>
                </div>
            )}

            {!loading && !error && widgets && (
                <>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7" data-testid="my-outreach-widgets">
                        {WIDGETS.map(([key, mineLabel, everyoneLabel]) => (
                            <div key={key} className="min-w-0 rounded-lg border border-stone-100 bg-white p-3" data-testid={`my-outreach-widget-${key}`}>
                                <p className={`text-xl font-semibold tabular-nums ${key === 'follow_ups_overdue' && widgets[key] ? 'text-red-700' : 'text-stone-900'}`}>{widgets[key] ?? 0}</p>
                                <p className="truncate text-xs text-stone-500">{!mine && everyoneLabel ? everyoneLabel : mineLabel}</p>
                            </div>
                        ))}
                    </div>
                    {data.coverage?.complete === false && (
                        <p className="text-xs text-amber-700" role="status" data-testid="my-outreach-partial">
                            Partial totals: {data.coverage.events_considered} of {data.coverage.events_matched} events were read. Narrow by event or date for exact figures.
                        </p>
                    )}
                    {widgets.contacts_owned === 0 ? (
                        <div className="rounded-xl border border-dashed border-stone-300 bg-white px-6 py-10 text-center" data-testid="my-outreach-empty">
                            <p className="font-semibold text-stone-800">{filtered ? 'No contacts match these filters' : mine ? 'None of your contacts are on an event yet' : 'No event outreach yet'}</p>
                            <p className="mt-1 text-sm text-stone-500">{filtered ? 'Clear a filter to widen the report.' : 'Add contacts to an event to start tracking invitations, RSVPs and follow-ups.'}</p>
                            {filtered && <button type="button" onClick={() => setFilters(EMPTY_FILTERS)} className="mt-3 text-sm font-medium underline">Clear filters</button>}
                        </div>
                    ) : (
                        <>
                            {data.needs_attention?.length > 0 && (
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="my-outreach-attention">
                                    <span className="inline-flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" />Needs attention</span>
                                    {data.needs_attention.map((item) => (
                                        <button key={item.code} type="button" onClick={() => navigate(eventTabHref(item.target.event_id, item.target.tab, item.target.filters))} className="underline decoration-amber-300 underline-offset-2">{attentionText(item)}</button>
                                    ))}
                                </div>
                            )}
                            <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
                                <Breakdown
                                    title="By event" icon={BarChart3} testId="my-outreach-by-event" columns={EVENT_COLUMNS}
                                    rows={(data.events || []).filter((row) => row.metrics?.on_list > 0)}
                                    rowKey={(row) => row.event_id}
                                    rowLabel={(row) => (
                                        <button type="button" onClick={() => navigate(eventTabHref(row.event_id, 'overview'))} className="max-w-full truncate text-left hover:underline">
                                            {row.name}<span className="ml-1 font-normal text-stone-400">{formatLocalDate(row.timing?.local_date)}</span>
                                        </button>
                                    )}
                                    emptyText="No event has contacts in this scope."
                                />
                                <Breakdown
                                    title="By Contact Owner" icon={Users} testId="my-outreach-by-owner" columns={OWNER_COLUMNS}
                                    rows={data.owners || []}
                                    rowKey={(row) => row.owner_id || 'unassigned'}
                                    rowLabel={(row) => row.name}
                                    emptyText="No Contact Owner has contacts in this scope."
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </section>
    );
}
